/* /educations - the basic course in full, plus a short pointer to the
   advanced one.

   This page is the site's best-performing result, so its URL, <title>, meta
   description and <h1> are deliberately untouched - only what sits under the
   H1 changed. The advanced course used to live here too; it now has its own
   page (see Pages/AdvancedCourse). */

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Bredcrumb from '../../HomeMain/Bredcrumb';
import Faq from '../../ui/Faq';
import CourseSignupForm from '../../Educations/CourseSignupForm';
import Testimonials from '../../Educations/Testimonials';
import {
  Section, Heading, P, NumberedList, FeatureList, InfoTable, Callout,
  buttonStyle, interpolate, BRAND, INK, BODY, serif, sans,
} from '../../ui/blocks';
import { BASIC_COURSE } from '../../../config/educations.mjs';
import OffersLink from '../../ui/OffersLink';
import { linkTo } from '../../../i18n/lang.mjs';

const CourseImg = 'https://res.cloudinary.com/dxa2jevmo/image/upload/v1761861334/IMG_3792_jcfgmv.jpg';

const ADVANCED = '/napredni-kurs-sminkanja';

const Main = () => {
  const { t, i18n } = useTranslation();
  const lang = ['sr', 'en', 'de'].includes(i18n.language) ? i18n.language : 'sr';
  const advancedHref = linkTo(ADVANCED, lang);

  const days = t('educations.basic.days.items', { returnObjects: true });
  const unseen = t('educations.basic.unseen.items', { returnObjects: true });
  const included = t('educations.basic.included.items', { returnObjects: true });
  const blocks = t('educations.basic.firstClient.blocks', { returnObjects: true });
  const faq = t('educations.basic.faq.items', { returnObjects: true });

  /* Dates and prices live in src/config/educations.mjs and are interpolated
     into the copy, so a new group means editing one file, not the locales. */
  const practicalRows = t('educations.basic.practical.rows', { returnObjects: true })
    .map((row) => ({
      label: row.label,
      value: interpolate(row.value, {
        max: BASIC_COURSE.maxAttendees,
        startDate: BASIC_COURSE.startDateLabel[lang] || BASIC_COURSE.startDateLabel.sr,
        price: BASIC_COURSE.priceEur,
        priceRsd: BASIC_COURSE.priceRsdApprox.toLocaleString('sr-RS'),
        deposit: BASIC_COURSE.depositEur,
      }),
    }));

  return (
    <>
      <Bredcrumb title={t('pages.educations.title')} subtitle={t('pages.educations.subtitle')} />

      {/* ── Which course is for you ── */}
      <Section>
        <Heading align="center">{t('educations.chooser.heading')}</Heading>
        <P style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 36px' }}>
          {t('educations.chooser.intro')}
        </P>

        <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
          {[
            { key: 'basic', href: `#bazni-kurs`, internal: false },
            { key: 'advanced', href: advancedHref, internal: true },
          ].map(({ key, href, internal }) => (
            <div
              key={key}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                padding: '28px',
                borderRadius: '20px',
                border: '1px solid rgba(144,81,15,0.16)',
                background: '#fff',
              }}
            >
              <h3 style={{ fontFamily: serif, fontSize: '22px', fontWeight: 600, color: INK, margin: 0, lineHeight: 1.3 }}>
                {t(`educations.chooser.${key}.title`)}
              </h3>
              <p style={{ fontFamily: sans, fontSize: '15px', lineHeight: 1.75, color: BODY, margin: 0, flex: 1 }}>
                {t(`educations.chooser.${key}.text`)}
              </p>
              {internal ? (
                <Link to={href} style={{ ...buttonStyle('outline'), alignSelf: 'flex-start' }}>
                  {t(`educations.chooser.${key}.cta`)}
                </Link>
              ) : (
                <a href={href} style={{ ...buttonStyle('solid'), alignSelf: 'flex-start' }}>
                  {t(`educations.chooser.${key}.cta`)}
                </a>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* ── Basic course ── */}
      <Section id="bazni-kurs" tinted>
        <Heading>{t('educations.basic.heading')}</Heading>
        <P>{t('educations.basic.lead1')}</P>
        <P>{t('educations.basic.lead2')}</P>
        <P style={{ fontWeight: 600, color: INK, marginBottom: 0 }}>{t('educations.basic.forWhom')}</P>
      </Section>

      <Section>
        <Heading>{t('educations.basic.days.heading')}</Heading>
        <P>{t('educations.basic.days.models')}</P>
        <div style={{ display: 'grid', gap: '16px', marginTop: '28px' }}>
          {days.map((day, i) => (
            <article
              key={i}
              style={{
                padding: '24px 26px',
                borderRadius: '18px',
                background: '#fff',
                border: '1px solid rgba(144,81,15,0.14)',
                borderLeft: `3px solid ${BRAND}`,
              }}
            >
              <h3 style={{ fontFamily: serif, fontSize: '21px', fontWeight: 600, color: INK, margin: '0 0 10px' }}>
                {day.title}
              </h3>
              <p style={{ fontFamily: sans, fontSize: '16px', lineHeight: 1.8, color: BODY, margin: 0 }}>
                {day.text}
              </p>
            </article>
          ))}
        </div>

        <img
          src={CourseImg}
          alt={t('educations.basic.heading')}
          loading="lazy"
          width="1170"
          height="1546"
          style={{ display: 'block', width: '100%', height: 'auto', borderRadius: '20px', marginTop: '32px' }}
        />
      </Section>

      <Section tinted>
        <Heading>{t('educations.basic.unseen.heading')}</Heading>
        <P>{t('educations.basic.unseen.intro')}</P>
        <NumberedList items={unseen} />
      </Section>

      <Section>
        <Heading>{t('educations.basic.included.heading')}</Heading>
        <FeatureList items={included} newBadge={t('educations.basic.included.newBadge')} />
      </Section>

      <Section tinted>
        <Heading>{t('educations.basic.firstClient.heading')}</Heading>
        <P>{t('educations.basic.firstClient.p1')}</P>
        <P>{t('educations.basic.firstClient.p2')}</P>
        <P>{t('educations.basic.firstClient.p3')}</P>
        <P style={{ fontWeight: 600, color: INK }}>{t('educations.basic.firstClient.p4')}</P>
        <div style={{ marginTop: '28px' }}>
          <FeatureList items={blocks} />
        </div>
      </Section>

      {/* Renders nothing at all until TESTIMONIALS has entries. */}
      <Testimonials kurs="basic" headingKey="educations.basic.testimonials.heading" />

      <Section>
        <Heading>{t('educations.basic.practical.heading')}</Heading>
        <InfoTable rows={practicalRows} />
        <Callout tone="gold">
          {t('educations.basic.practical.payback', {
            from: BASIC_COURSE.marketPriceRsd.from.toLocaleString('sr-RS'),
            to: BASIC_COURSE.marketPriceRsd.to.toLocaleString('sr-RS'),
          })}
        </Callout>
        <Callout>
          {t('educations.basic.practical.earlyBird', {
            deadline: BASIC_COURSE.earlyBird.deadlineLabel[lang] || BASIC_COURSE.earlyBird.deadlineLabel.sr,
          })}
        </Callout>
      </Section>

      <Section tinted>
        <Heading>{t('educations.basic.outOfTown.heading')}</Heading>
        <P>{t('educations.basic.outOfTown.p1')}</P>
        <P style={{ marginBottom: 0 }}>{t('educations.basic.outOfTown.p2')}</P>
      </Section>

      <Section>
        <Heading align="center">{t('educations.basic.faq.heading')}</Heading>
        <Faq items={faq} />
      </Section>

      <CourseSignupForm defaultCourse="basic" />

      {/* ── Pointer to the advanced course ── */}
      <Section>
        <div style={{ textAlign: 'center' }}>
          <Heading align="center">{t('educations.advancedTeaser.heading')}</Heading>
          <P style={{ maxWidth: '640px', margin: '0 auto 28px' }}>
            {t('educations.advancedTeaser.text')}
          </P>
          <Link to={advancedHref} style={buttonStyle('solid')}>
            {t('educations.advancedTeaser.cta')}
          </Link>
          <div style={{ marginTop: '26px' }}>
            <OffersLink />
          </div>
        </div>
      </Section>
    </>
  );
};

export default Main;
